import DOMPurify, { type Config } from "dompurify";

// Safe HTML config for rendering rich-text content (KB articles, etc.).
// Allows common formatting + images + iframes (YouTube embeds) with restrictions.
const RICH_TEXT_CONFIG: Config = {
  ALLOWED_TAGS: [
    "p", "br", "hr", "span", "div",
    "h1", "h2", "h3", "h4",
    "strong", "em", "u", "s", "code", "pre", "blockquote",
    "ul", "ol", "li",
    "a", "img",
    "iframe", // YouTube embeds
    "table", "thead", "tbody", "tr", "th", "td", "colgroup", "col",
  ],
  ALLOWED_ATTR: [
    "href", "target", "rel",
    "src", "alt", "title",
    "width", "height",
    "colspan", "rowspan", "style",
    "class",
    // iframe allowlist (TipTap YouTube embed sets these)
    "frameborder", "allow", "allowfullscreen",
  ],
  ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
  // Only allow iframes from trusted video providers
  ADD_TAGS: [],
  ADD_ATTR: [],
};

// DOMPurify hook: restrict iframe src to YouTube / Vimeo only.
DOMPurify.addHook("uponSanitizeElement", (node, data) => {
  if (data.tagName === "iframe") {
    const src = (node as HTMLIFrameElement).getAttribute("src") || "";
    const ok = /^https?:\/\/(www\.)?(youtube\.com|youtube-nocookie\.com|player\.vimeo\.com)\//i.test(src);
    if (!ok) {
      node.parentNode?.removeChild(node);
    }
  }
});

// External links open in new tab and get rel="noopener noreferrer".
DOMPurify.addHook("afterSanitizeAttributes", (node) => {
  if (node.tagName === "A" && (node as HTMLAnchorElement).getAttribute("href")?.startsWith("http")) {
    node.setAttribute("target", "_blank");
    node.setAttribute("rel", "noopener noreferrer");
  }
  // Legacy fix: early uploads embedded the raw private-bucket S3 URL, which
  // renders as a broken image (403). Rewrite to the auth-gated proxy.
  if (node.tagName === "IMG") {
    const src = (node as HTMLImageElement).getAttribute("src") || "";
    const m = src.match(/^https?:\/\/[^/]+\.s3\.[^/]+\.amazonaws\.com\/(v3\/uploads\/.+)$/i);
    if (m) {
      (node as HTMLImageElement).setAttribute("src", `/api/uploads/file/${m[1]}`);
    }
  }
});

export function sanitizeRichTextHtml(html: string): string {
  if (!html) return "";
  return DOMPurify.sanitize(html, RICH_TEXT_CONFIG) as unknown as string;
}
